import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, MapPin, Calendar, ImagePlus, ChevronRight, Loader2, Trash2, X, Check, Share2, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { JournalShareDialog } from '../components/JournalShareDialog';

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

const DeletePhotosModal = ({ journal, apiUrl, token, onClose, onRefresh }) => {
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await api.get(`/journal/${journal.itinerary_id}/photos`);
        setPhotos(res.data.photos || []);
      } catch { /* silent */ } finally { setLoading(false); }
    };
    fetchPhotos();
    // api, setPhotos, setLoading are stable refs
  }, [journal.itinerary_id]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === photos.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(photos.map(p => p.id)));
    }
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      await Promise.all([...selected].map(id => api.delete(`/journal/${journal.itinerary_id}/photos/${id}`)));
      toast.success(`Deleted ${selected.size} photo(s)`);
      onRefresh();
      onClose();
    } catch {
      toast.error('Failed to delete photos');
    } finally { setDeleting(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="delete-photos-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-ocean/10 flex items-center justify-between">
          <div>
            <h3 className="font-serif text-lg text-ocean font-bold">Manage Photos</h3>
            <p className="text-charcoal/50 text-xs mt-0.5">{journal.destination_city} — {photos.length} photo(s)</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-ocean/5 hover:bg-ocean/10 flex items-center justify-center">
            <X className="w-4 h-4 text-charcoal/50" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-ocean" /></div>
          ) : photos.length === 0 ? (
            <p className="text-charcoal/40 text-sm text-center py-10">No photos found</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map(photo => (
                <div
                  key={photo.id}
                  onClick={() => toggleSelect(photo.id)}
                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all bg-ocean/5 ${selected.has(photo.id) ? 'border-red-500 ring-2 ring-red-200' : 'border-transparent hover:border-ocean/20'}`}
                  data-testid={`select-photo-${photo.id}`}
                >
                  <img
                    src={`${apiUrl}/api/journal/file/${photo.storage_path}`}
                    alt={photo.caption || ''}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {selected.has(photo.id) && (
                    <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    </div>
                  )}
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-0.5">
                      <p className="text-white text-[10px] truncate">{photo.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {photos.length > 0 && (
          <div className="p-4 border-t border-ocean/10 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={selectAll}
              className="text-ocean text-xs h-8 px-3" data-testid="select-all-btn">
              {selected.size === photos.length ? 'Deselect All' : 'Select All'}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-charcoal/40 text-xs">{selected.size} selected</span>
              <Button size="sm" onClick={handleDelete} disabled={selected.size === 0 || deleting}
                className="bg-red-500 hover:bg-red-600 text-white h-8 px-4 rounded-lg text-xs" data-testid="delete-selected-btn">
                {deleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Delete{selected.size > 0 ? ` (${selected.size})` : ''}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

const JournalCard = ({ journal, apiUrl, token, onRefresh }) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareCardData, setShareCardData] = useState(null);

  const handleShare = async (e) => {
    e.stopPropagation();
    try {
      const res = await api.get(`/journal/${journal.itinerary_id}/photos`);
      const photos = res.data.photos || [];
      if (photos.length === 0) { toast.error('No photos to share'); return; }
      const shareRes = await api.post('/journal/share', {
        itinerary_id: journal.itinerary_id,
        photo_ids: photos.map(p => p.id),
      });
      if (shareRes.data.success) {
        const shareUrl = `${window.location.origin}/shared/journal/${shareRes.data.share_id}`;
        setShareCardData({
          photoUrl: `${apiUrl}/api/journal/file/${photos[0].storage_path}`,
          galleryUrl: shareUrl,
          photoCount: photos.length,
        });
        setShowShareDialog(true);
      }
    } catch { toast.error('Failed to share'); }
  };

  return (
    <motion.div variants={fadeUp}>
      <Card
        className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
        onClick={() => navigate(`/itinerary/${journal.itinerary_id}?tab=journal`)}
        data-testid={`journal-card-${journal.itinerary_id}`}
      >
        {/* Photo preview grid */}
        <div className="grid grid-cols-2 gap-0.5 h-44 overflow-hidden bg-ocean/5">
          {journal.preview_paths.length > 0 ? (
            journal.preview_paths.slice(0, 4).map((path, i) => (
              <div key={`preview-${path}`} className={`relative overflow-hidden bg-ocean/5 ${journal.preview_paths.length === 1 ? 'col-span-2 row-span-2' : journal.preview_paths.length === 2 ? 'row-span-2' : journal.preview_paths.length === 3 && i === 0 ? 'row-span-2' : ''}`}>
                <img
                  src={`${apiUrl}/api/journal/file/${path}`}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                />
              </div>
            ))
          ) : (
            <div className="col-span-2 row-span-2 bg-ocean/5 flex items-center justify-center">
              <ImagePlus className="w-10 h-10 text-ocean/20" />
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-serif text-lg text-ocean font-bold truncate">
                {journal.destination_city}
              </h3>
              {journal.destination_country && (
                <p className="text-charcoal/50 text-xs flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {journal.destination_country}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 mt-1">
              <button
                onClick={handleShare}
                className="w-8 h-8 rounded-full bg-ocean/10 hover:bg-ocean/20 text-ocean flex items-center justify-center transition-colors"
                data-testid={`share-journal-${journal.itinerary_id}`}
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }}
                className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 flex items-center justify-center transition-colors"
                data-testid={`delete-journal-${journal.itinerary_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <ChevronRight className="w-5 h-5 text-ocean/30 group-hover:text-ocean group-hover:translate-x-0.5 transition-all" />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3 text-xs text-charcoal/50">
            {journal.start_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {journal.start_date}
              </span>
            )}
            <span className="flex items-center gap-1 font-medium text-ocean">
              <BookOpen className="w-3 h-3" />
              {journal.photo_count} photo{journal.photo_count !== 1 ? 's' : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeletePhotosModal
          journal={journal}
          apiUrl={apiUrl}
          token={token}
          onClose={() => setShowDeleteModal(false)}
          onRefresh={onRefresh}
        />
      )}

      {showShareDialog && shareCardData && (
        <JournalShareDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          photoUrl={shareCardData.photoUrl}
          destination={journal.destination_city}
          country={journal.destination_country}
          date={journal.start_date}
          photoCount={shareCardData.photoCount}
          galleryUrl={shareCardData.galleryUrl}
        />
      )}
    </motion.div>
  );
};

export default function MyJournalPage() {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const refreshJournals = async () => {
    try {
      const res = await api.get('/journal/my-journals');
      if (res.data.success) setJournals(res.data.journals);
    } catch { /* silent */ }
  };

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const res = await api.get('/journal/my-journals');
        if (res.data.success) setJournals(res.data.journals);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    };
    fetchJournals();
  }, []);

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
      <Header />

      <main className="pt-24 pb-20 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          {/* Page header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="font-serif text-3xl sm:text-4xl text-white font-bold flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-gold" /> My Journal
            </h1>
            <p className="text-white/60 text-sm mt-2">
              Your travel memories, organized by trip
            </p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
            </div>
          ) : journals.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-lg">
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-ocean/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ImagePlus className="w-8 h-8 text-ocean/40" />
                  </div>
                  <h3 className="font-serif text-xl text-ocean font-bold mb-2">No Photos Yet</h3>
                  <p className="text-charcoal/50 text-sm mb-6 max-w-md mx-auto">
                    Start capturing your travel memories! Open any trip and use the Journal tab to upload photos.
                  </p>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-gold hover:bg-gold-dark text-ocean font-semibold rounded-lg px-6"
                    data-testid="go-to-trips-btn"
                  >
                    Go to My Trips
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              initial="hidden"
              animate="show"
              variants={stagger}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {journals.map(j => (
                <JournalCard key={j.itinerary_id} journal={j} apiUrl={apiUrl} token={token} onRefresh={refreshJournals} />
              ))}
            </motion.div>
          )}

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
